import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp, User, Settings, CreditCard, LogOut,
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * ProfileMenu — bottom-left of the sidebar.
 *
 * Two visual states:
 *   • Collapsed (default): a compact row showing avatar, email,
 *     plan badge, and a chevron-up. Click to open the popup.
 *   • Expanded: a card pops up ABOVE the collapsed row with the
 *     same avatar + email + plan, then a divider, then four
 *     actions: My Profile, Settings, Billing, Logout.
 *
 * Click outside or the X button to collapse.
 * The collapsed row stays pinned to the bottom of the sidebar.
 */
export function ProfileMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    // Use mousedown so the click that opens the menu doesn't immediately close it
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!user) return null;

  // Best-effort display name priority:
  //   2. user_metadata.first_name + last_name (set at signup)
  //   3. Google OAuth: user_metadata.full_name or name
  //   4. Fallback to email-derived name
  const email = user.email || '';
  const meta = user.user_metadata || {};
  let displayName = '';
  if (meta.display_name && meta.display_name.trim()) {
    displayName = meta.display_name.trim();
  } else if ((meta.first_name || meta.last_name) && (meta.first_name || '').trim() || (meta.last_name || '').trim()) {
    displayName = `${(meta.first_name || '').trim()} ${(meta.last_name || '').trim()}`.trim();
  } else if (meta.full_name && meta.full_name.trim()) {
    displayName = meta.full_name.trim();
  } else if (meta.name && meta.name.trim()) {
    displayName = meta.name.trim();
  } else if (email.includes('@')) {
    displayName = email.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  } else {
    displayName = email || 'User';
  }
  const planCode = user.plan_code || 'free';
  const initial = displayName ? displayName[0].toUpperCase() : (email[0] ? email[0].toUpperCase() : '?');

  function go(path) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div
      ref={containerRef}
      className="relative p-3 border-t border-border"
    >
      {/* Expanded popup — rendered above the collapsed row */}
      {open && (
        <div
          className="absolute bottom-full left-3 right-3 mb-2 bg-surface border border-border rounded-xl overflow-hidden shadow-[0_4px_24px_-4px_rgba(52,58,64,0.10),0_2px_6px_rgba(52,58,64,0.04)] animate-fade-in z-30"
          role="menu"
        >
          {/* User info */}
          <div className="p-4 pb-3">
            <div className="text-sm font-semibold text-fg truncate">
              {displayName}
            </div>
            <div className="text-xs text-fg-muted truncate">
              {email}
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Menu items */}
          <div className="py-1">
            <MenuItem
              icon={User}
              label="My Profile"
              onClick={() => go('/profile')}
            />
            <MenuItem
              icon={Settings}
              label="Settings"
              onClick={() => go('/settings')}
            />
            <MenuItem
              icon={CreditCard}
              label="Billing"
              onClick={() => go('/billing')}
            />
          </div>

          <div className="border-t border-border" />

          <div className="py-1">
            <MenuItem
              icon={LogOut}
              label="Logout"
              accent="brand"
              onClick={() => {
                setOpen(false);
                onSignOut?.();
              }}
            />
          </div>
        </div>
      )}

      {/* Collapsed row — always visible at the bottom of the sidebar */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-2.5 p-2 rounded-md text-left transition-colors',
          'hover:bg-surface-2',
          open && 'bg-surface-2'
        )}
      >
        {/* Avatar — orange brand tint */}
        <div className="h-7 w-7 rounded-full bg-brand-softest border border-brand-soft flex items-center justify-center text-brand font-semibold text-xs shrink-0">
          {initial}
        </div>

        {/* Name + email */}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-fg truncate">
            {displayName}
          </div>
          <div className="text-2xs text-fg-muted truncate">
            {email}
          </div>
        </div>

        {/* Chevron up */}
        <ChevronUp
          className={cn(
            'h-3.5 w-3.5 text-fg-subtle shrink-0 transition-transform',
            !open && 'rotate-180'
          )}
        />
      </button>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, accent }) {
  // accent: 'brand' makes the item orange (used for Logout).
  // Default is 'fg' (regular text color for My Profile, Settings, Billing).
  const accentClass =
    accent === 'brand'
      ? 'text-brand hover:bg-brand-softest'
      : 'text-fg hover:bg-surface-2';
  return (
    <button
      type="button"
      onClick={onClick}
      role="menuitem"
      className={cn(
        'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors text-left',
        accentClass
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </button>
  );
}